import {LocationManager} from './util/LocationManager';
import {getAppStore_DoNotCall} from './AppStore';
import {SyncService} from './services/SyncService';
import {FolderLocation, FolderLocationPropertyNames, selectInboxFolderByDefault} from './locations/FolderLocation';
import {EmailLocation, EmailLocationPropertyNames} from './locations/EmailLocation';
import {SystemActions} from './actions/SystemActions';
import {registerSearchTextSubjectHandler} from './subjects/SearchSubjectHandler';
import {EmailActions} from './actions/EmailActions';
import {FolderActions} from './actions/FolderActions';
import {CalendarLocation, CalendarLocationPropertyNames} from './locations/CalendarLocation';
import {SystemStatusLocation, SystemStatusLocationPropertyNames} from "./locations/SystemStatusLocation";
import {SystemStatusService} from "./services/system/SystemStatusService";
import {ContentLocation, ContentLocationPropertyNames} from "./locations/ContentLocation";
import {ServiceWorkerMessageHandler} from "./locations/ServiceWorkerMessageHandler";
import {NotificationService} from "./services/NotificationService";
import {decodePgpKeyDtos, getSecretKeysFromWebService} from "./util/PGPUtil";
import {NotesLocation, NotesLocationPropertyNames} from "./locations/NotesLocation";
import {DB} from "./db/DbManager";
import {ContactActions} from './redux/ContactSlice';
import {SyncActions} from "./redux/SyncSlice";
import {AccountsActions} from "./redux/AccountsSlice";
import {PgpKeysLocation, PgpKeysLocationPropertyNames} from "./locations/PgpKeysLocation";
import {UsersLocation, UsersLocationPropertyNames} from "./locations/UsersLocation";
import {NotesActions} from "./redux/NotesSlice";
import {registerAttachmentSearchTextSubjectHandler} from "./subjects/AttachmentSearchSubjectHandler";
import {registerPhotosSearchTextSubjectHandler} from "./subjects/PhotosSearchSubjectHandler";
import {AndroidMessageHandler} from "./android/AndroidMessageHandler";
import {isAndroidApp} from "./util/DeviceUtil";
import {updateDateRangeCategories} from "./util/DateHelper";
import {DateTime} from "luxon";
// import {initWorker} from "openpgp";
import {AllCommunityModule, ModuleRegistry} from 'ag-grid-community';
import {BatchEditModule, LicenseManager, RowGroupingModule} from 'ag-grid-enterprise';
import {registerNotesSearchTextSubjectHandler} from "./subjects/NotesSearchSubjectHandler";

const store = getAppStore_DoNotCall();

const _locationManager = new LocationManager(store);
export const systemStatusService = new SystemStatusService(store);

function promiseRejectionHandler(event: PromiseRejectionEvent) {
    // TODO: show a warning on the UI?
    console.log("Unhandled rejection", event.reason, event);
}

export function bootstrapUser(syncService?: SyncService) {
    const userName = store.getState().system.userName;

    if (userName.length > 0) {
        // tslint:disable-next-line: no-floating-promises
        DB.init(userName)
            .then(async () => {
                const folders = await DB.getFolders();
                const syncState = await DB.getSyncStates();
                const operations = await DB.getAllOperations();
                const draftEmailHeaders = (await DB.getAllDraftEmails())?.filter(d => d.IsDraft).map(EmailActions.convertDraftToEmailHeader);
                const contacts = await DB.getAllContacts() || [];
                const davContacts = await DB.getAllDavContacts() || [];

                let encodedSecretKeys = await DB.getAllSecretKeys();

                const noteSections = await DB.getAllNoteSections();

                store.dispatch(NotesActions.noteSectionsLoaded(noteSections));

                store.dispatch(EmailActions.draftEmailsLoaded(draftEmailHeaders ?? []));
                store.dispatch(FolderActions.foldersLoaded(folders));
                store.dispatch(SyncActions.setOperations({operations}));
                store.dispatch(SyncActions.setSyncState(syncState));

                store.dispatch(ContactActions.contactsLoaded(contacts));
                store.dispatch(ContactActions.davContactsLoaded(davContacts));

                if (encodedSecretKeys.length === 0) {
                    try {
                        encodedSecretKeys = await getSecretKeysFromWebService();
                    } catch (e: any) {
                        console.error("Error retrieving secret keys", e);
                    }
                }
                const secretKeys = await decodePgpKeyDtos(encodedSecretKeys, []) || [];

                store.dispatch(AccountsActions.setSecretKeys({keys: secretKeys, loadedIfEmpty: true}));

                syncService?.start();

                _locationManager.startListening();

                new NotificationService(store).start();

                if (locationManager.getCurrentApp() === "email") {
                    selectInboxFolderByDefault(store);
                }
            });
        return true;
    } else {
        return false;
    }
}

export function startApplication() {
    updateDateRangeCategories(DateTime.local());

    // TODO: don't use all modules here!
    ModuleRegistry.registerModules([AllCommunityModule, RowGroupingModule, BatchEditModule]);
    LicenseManager.setLicenseKey(
        "Using_this_{AG_Grid}_Enterprise_key_{AG-052319" +
        "}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid" +
        ".com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Integrated_Holding_Group,_Lp" +
        ".}_is_granted_a_{Multiple_Applications}_Developer_License_for_{15}_Front-End_JavaScript_developers___All_Front" +
        "-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid" +
        "}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_" +
        "{31_December_2025}____[v3]_[01]_MTc2NzEzOTIwMDAwMA==010169dc0d610eec4213b5811b0de422");

    window.addEventListener('unhandledrejection', promiseRejectionHandler)

    // TODO: support web workers for offloading the PGP decryption/encryption
    // initWorker({path: 'openpgp/openpgp.worker.min.js'});

    const syncService = new SyncService(store, DB);

    if (!bootstrapUser(syncService)) {
        syncService.start();
    }

    function handleWindowResize() {
        store.dispatch(SystemActions.resizeWindow(window.innerWidth));
    }

    window.addEventListener("resize", () => handleWindowResize());

    handleWindowResize();

    registerSearchTextSubjectHandler(store);
    registerAttachmentSearchTextSubjectHandler(store);
    registerPhotosSearchTextSubjectHandler(store);
    registerNotesSearchTextSubjectHandler(store);

    _locationManager.setAppProperties("email", [...EmailLocationPropertyNames, ...FolderLocationPropertyNames, ...CalendarLocationPropertyNames, ...ContentLocationPropertyNames, ...NotesLocationPropertyNames]);
    _locationManager.addListener("email", FolderLocation.handleLocationChange);
    _locationManager.addListener("email", EmailLocation.handleLocationChange);
    _locationManager.addListener("email", CalendarLocation.handleLocationChange);
    _locationManager.addListener("email", ContentLocation.handleLocationChange);
    _locationManager.addListener("email", NotesLocation.handleLocationChange);

    _locationManager.setAppProperties("system", SystemStatusLocationPropertyNames);
    _locationManager.addListener("system", SystemStatusLocation.handleLocationChange);

    _locationManager.setAppProperties("pgpkeys", PgpKeysLocationPropertyNames);
    _locationManager.addListener("pgpkeys", PgpKeysLocation.handleLocationChange);

    _locationManager.setAppProperties("users", UsersLocationPropertyNames);
    _locationManager.addListener("users", UsersLocation.handleLocationChange);

    _locationManager.setAppProperties("rules", ContentLocationPropertyNames);
    _locationManager.addListener("rules", ContentLocation.handleLocationChange);

    ServiceWorkerMessageHandler.addServiceWorkerMessageListener("openEmail", (data) => EmailLocation.handleOpenWindowMessage(store.dispatch, data));
    ServiceWorkerMessageHandler.addServiceWorkerMessageListener("openCalendar", (data) => CalendarLocation.handleOpenWindowMessage(data));
    ServiceWorkerMessageHandler.addServiceWorkerMessageListener("openContact", (data) => ContentLocation.handleOpenWindowMessage(data));

    ServiceWorkerMessageHandler.listenForServiceWorkerMessages();

    if (isAndroidApp()) {
        const handler = new AndroidMessageHandler(store);
        handler.listenForMessages();
        AndroidMessageHandler.init();
    }

    setInterval(() => {
        console.log("Updating date range categories");
        updateDateRangeCategories(DateTime.local());
        // TODO: refresh the emails shown in the current folder
        // const state = store.getState();
        // store.dispatch(EmailActions.showEmailHeaders(state.itemState.emailHeaders, ));
    }, 1000 * 60 * 60);
}

export function getStore_DoNotCall() {
    return store;
}

export type VisibilityChangeHandler = (visible: boolean) => void;

export function registerVisibilityChangeHandler(handler: VisibilityChangeHandler) {
    document.addEventListener('visibilitychange', function () {
        handler(document.hidden);
    });
}

export const locationManager = _locationManager;

